import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UsageWarning {
  appName: string;
  current: number;
  limit: number;
  percentage: number;
  severity: 'warning' | 'critical';
}

export function useGlobalUsageMonitor() {
  const [warnings, setWarnings] = useState<UsageWarning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    fetchUsageWarnings();
  }, []);

  const fetchUsageWarnings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if dismissed for this month
      const dismissKey = `usage-warning-dismissed-${new Date().getMonth()}`;
      const dismissed = localStorage.getItem(dismissKey);
      if (dismissed === 'true') {
        setIsDismissed(true);
        setIsLoading(false);
        return;
      }

      // Get user's subscription plan
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_type')
        .eq('user_id', user.id)
        .maybeSingle();

      const planType = subscription?.plan_type || 'basic';

      // Get all enabled apps for user's plan
      const { data: planFeatures } = await supabase
        .from('plan_features')
        .select('app_name, usage_limit')
        .eq('plan_type', planType)
        .eq('is_enabled', true)
        .not('usage_limit', 'is', null)
        .neq('usage_limit', -1); // Exclude unlimited

      if (!planFeatures || planFeatures.length === 0) {
        setIsLoading(false);
        return;
      }

      // Get current usage for this month
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const appNames = planFeatures.map(f => f.app_name);

      const { data: usage } = await supabase
        .from('user_usage_tracking')
        .select('app_name, current_usage')
        .eq('user_id', user.id)
        .in('app_name', appNames)
        .gte('period_start', periodStart.toISOString());

      const usageMap = new Map(
        usage?.map(u => [u.app_name, u.current_usage]) || []
      );

      // Calculate warnings for apps at 80%+ usage
      const newWarnings: UsageWarning[] = [];

      for (const feature of planFeatures) {
        const current = usageMap.get(feature.app_name) || 0;
        const limit = feature.usage_limit;
        const percentage = (current / limit) * 100;

        if (percentage >= 80) {
          newWarnings.push({
            appName: feature.app_name,
            current,
            limit,
            percentage,
            severity: percentage >= 95 ? 'critical' : 'warning'
          });
        }
      }

      setWarnings(newWarnings);
    } catch (error) {
      console.error('Failed to fetch usage warnings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissWarning = () => {
    const dismissKey = `usage-warning-dismissed-${new Date().getMonth()}`;
    localStorage.setItem(dismissKey, 'true');
    setIsDismissed(true);
  };

  return {
    warnings,
    isLoading,
    isDismissed,
    dismissWarning,
    hasWarnings: warnings.length > 0 && !isDismissed
  };
}
